import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import hash from 'object-hash';

import { getSecureValue, isWeb, setSecureValue } from '@helpers';
import { msalInstance } from '@config/msalConfig';
import { setMsalSession } from '@config/msalSession';
import { setPersistUserEmail } from '@store/persistUserScope';
import { initSignalR } from '@reducers/signalR';
import { getAllFeatureFlags, generateTokenInfo, getAuthInfo, resolveLanguageWithPrefix } from '@reducers/user';
import { getConnectorInfo } from '@reducers/connector';
import { connectorProductionConfigurationDetails, deviceAliveEvent } from '@reducers/membership';
import { validateOnboarding } from '@reducers/onBoarding';
import { updateDevice } from '@reducers/device';
import { getTimeSetup } from '@reducers/time';
import DeviceInfo from 'react-native-device-info';
import { scale, notchTop } from '@components/frame';
import { unlogin } from '@reducers/utils';

import ColorWrapper from '@styles/colors';

const SignaR = ({
  deviceAliveEvent,
  initSignalR,
  getAllFeatureFlags,
  connectorProductionConfigurationDetails,
  getConnectorInfo,
  getAuthInfo,
  getTimeSetup,
  updateAppView,
  C,
  signalR: { appHubReconnecting, signalRAnonymReconnecting },
  updateDevice,
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  initializeApp,
  validateOnboarding,
  unlogin,
  lg,
  updateLanguage,
  useMsal,
}) => {
  const { instance } = useMsal ? useMsal() : {};

  const reconnecting = appHubReconnecting || signalRAnonymReconnecting;

  useEffect(() => {
    let deviceInfoTimeoutId = null;

    // TODO: SF-4988 — disabled, incompatible with multi-account (different tabs may have different users)
    // const storageEvent = (e) => {
    //   if (e.key === 'status' && e.newValue !== 'active') {
    //     unlogin();
    //   }
    // };

    (async () => {
      if (isWeb) {
        try {
          instance
            .handleRedirectPromise()
            .then(async (response) => {
              if (response) {
                setMsalSession(response.account, response.accessToken);
              }
              setSecureValue('tokenUpdating', 'false');
            })
            .catch(async (e) => {
              setSecureValue('tokenUpdating', 'false');
            });
        } catch (error) {
          setSecureValue('tokenUpdating', 'false');
        }
      }

      await getTimeSetup();

      const isAuthorized = getSecureValue('token');
      if (!isAuthorized) {
        return;
      }

      // On web, ensure persist scope matches the active account and refresh user info.
      if (isWeb) {
        const active = msalInstance.getActiveAccount();
        if (active) setPersistUserEmail(active.username);
        getAuthInfo();
      }

      // TODO: SF-4988 — disabled, incompatible with multi-account
      // if (isWeb) {
      //   window.addEventListener('storage', storageEvent);
      // }

      const supported = isSupported && (await isSupported());
      if (isWeb && supported) {
        try {
          const firebaseConfig = {
            apiKey: 'AIzaSyCBLvRFUoKeIcB2_N6mdr3LG-T2X8s2gt0',
            authDomain: 'smartfactory-e7947.firebaseapp.com',
            projectId: 'smartfactory-e7947',
            storageBucket: 'smartfactory-e7947.appspot.com',
            messagingSenderId: '546828331859',
            appId: '1:546828331859:web:e59bbc621374b4bbf50fa6',
            measurementId: 'G-VXHGJLVZ2P',
          };

          const firebaseApp = initializeApp(firebaseConfig);
          const messaging = getMessaging(firebaseApp);

          if ('serviceWorker' in navigator) {
            (async () => {
              try {
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
                console.log('Registration successful, scope is:', registration.scope);

                const currentToken = await getToken(messaging, { serviceWorkerRegistration: registration });
                if (currentToken) {
                  console.log('current token for client: ', currentToken);
                  console.log('HASH', hash({ currentToken }));
                  setSecureValue('getUniqueId', hash({ currentToken }));
                  updateDevice(currentToken);
                  onMessage(messaging, (m) => {
                    console.log('Message', m);
                    new Notification(m.notification.title, {
                      body: m.notification.body,
                    });
                  });
                } else {
                  console.log('No registration token available.');
                }
              } catch (err) {
                console.log('Service worker / token error:', err);
              }
            })();
          }
        } catch (error) {
          console.error(error);
        }
      }

      setTimeout(async () => {
        initSignalR({
          callbacks: {
            appHub: async () => {
              await updateDeviceInfo();
              await getConnectorInfo();
            },
            signalRAnonym: validateOnboarding,
          },
        });

        const [configResult, authResult] = await Promise.allSettled([
          connectorProductionConfigurationDetails(),
          getAuthInfo(false, { lg, updateLanguage }),
          getAllFeatureFlags(),
          generateTokenInfo(),
          getConnectorInfo(),
        ]);

        const configData = configResult.status === 'fulfilled' ? configResult.value : undefined;
        const authData = authResult.status === 'fulfilled' ? authResult.value : undefined;
        const prefix = configData?.sourceSystems?.[0]?.code;
        if (prefix) {
          resolveLanguageWithPrefix(authData?.userProfile?.preferedLanguage, { lg, updateLanguage }, prefix);
        }

        await updateAppView();
      }, 1500);
    })();

    const updateDeviceInfo = async () => {
      const deviceId = isWeb ? getSecureValue('getUniqueId') : await DeviceInfo.getUniqueId();
      const token = getSecureValue('token');
      if (deviceId && (token || isWeb)) {
        const responce = await deviceAliveEvent(deviceId);
        if (responce?.processedOn) {
          deviceInfoTimeoutId = setTimeout(() => {
            updateDeviceInfo();
          }, 1000 * 60 * 2);
        } else {
          deviceInfoTimeoutId = setTimeout(() => {
            updateDeviceInfo();
          }, 1000 * 60 * 10);
        }
      }
    };

    return () => {
      if (deviceInfoTimeoutId) {
        clearTimeout(deviceInfoTimeoutId);
        deviceInfoTimeoutId = null;
      }
      // TODO: SF-4988 is disabled because incompatible with multi-account
      // if (isWeb) {
      //   window.removeEventListener('storage', storageEvent);
      // }
    };
  }, []);

  return reconnecting ? (
    <View
      style={{
        width: '100%',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        zIndex: 10,
        top: isWeb ? scale(60) : scale(22) + notchTop,
        pointerEvents: 'none',
      }}
    >
      <View
        style={{
          backgroundColor: C('Overlay/Light/Dim strong'),
          minWidth: scale(96),
          minHeight: scale(16),
          borderRadius: scale(12),
          paddingHorizontal: scale(8),
          paddingVertical: scale(2),
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text style={[{ color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('Restoring Live Connection...')}</Text>
      </View>
    </View>
  ) : (
    <View />
  );
};

const mapStateToProps = ({ signalR }) => ({ signalR });

export default connect(mapStateToProps, {
  initSignalR,
  getAllFeatureFlags,
  connectorProductionConfigurationDetails,
  getConnectorInfo,
  deviceAliveEvent,
  getAuthInfo,
  getTimeSetup,
  updateDevice,
  validateOnboarding,
  unlogin,
})(ColorWrapper(SignaR));
